export enum MediaCategory {
  Photo = 'Photo',
  Document = 'Document',
  FloorPlan = 'Floor Plan',
  Tour = 'Unbranded Virtual Tour',
}

export interface MediaResource {
  MEDIA_CATEGORY: string;
  MEDIA_DISPLAY_ORDER: number;
  MEDIA_ITEM_NUMBER: number;
  MEDIA_LONG_DESCRIPTION?: string;
  MEDIA_SHORT_DESCRIPTION?: string;
  MEDIA_IMAGE_WIDTH?: number;
  MEDIA_IMAGE_HEIGHT?: number;
  MEDIA_MODIFICATION_TIMESTAMP: string;
  MEDIA_URL_MEDIUM?: string;
  MEDIA_URL?: string;
  MED_VW_KEY: string;
}

export interface MediaUpdate {
  category: MediaCategory | MediaCategory[];
  resources: MediaResource[];
}

export interface MediaLimits {
  count: number;
  size: string; // "20mb" or "500kb" format
}

export enum MediaType {
  Photo = 'photos',
  Tour = 'tours',
  Document = 'docs',
}

export interface MediaConfiguration {
  limits: {
    [key in MediaType]: MediaLimits;
  };
}

export interface ValidationFailure {
  file: File;
  reason: 'count' | 'size' | 'name';
  limit: string;
}

export interface MediaPollingOptions {
  pollInterval: number;
  maxPollAttempts: number;
  maxShadowFailures: number;
}

export interface ValidationResult {
  validFiles: File[];
  countFailures: ValidationFailure[];
  sizeFailures: ValidationFailure[];
  nameFailures: ValidationFailure[];
}


/**
 * Utility functions for media-related operations
 */

export interface MediaError {
  isMimeTypeError: boolean;
  originalError: Error;
  message: string;
}

/**
 * Check if an error message contains "Invalid MimeType null"
 *
 * Example error format:
 * "SubSystem(Servlet) = 20029 - The following argument is invalid for this service call - Message = Invalid MimeType null"
 */
export const isMimeTypeNullError = (error: any): boolean => {
  if (!error) return false;

  const errorMessage = error instanceof Error ? error.message : String(error);

  // Check for the exact error message pattern
  return errorMessage.includes('Invalid MimeType null');
};

/**
 * Parse and categorize a media error
 */
export const parseMediaError = (error: any): MediaError => {
  const isMimeTypeError = isMimeTypeNullError(error);

  return {
    isMimeTypeError,
    originalError: error instanceof Error ? error : new Error(String(error)),
    message: error instanceof Error ? error.message : String(error),
  };
};

/**
 * Translate media record from RESO format (CWA prefixes) to local format
 *
 * @param mediaRecord - The media record with CWA prefixed keys
 * @param translation - The translation object from locale()
 * @returns Translated media record with local format keys
 *
 * @example
 * const translation = locale();
 * const localMedia = translateMediaRecord(resoMedia, translation);
 */
export const translateMediaRecord = (
  mediaRecord: any,
  translation: any,
): any => {
  const getTranslationKey = (resoKey: string) => {
    for (let key in translation.Media) {
      const localeKey = translation.Media[key];
      if (localeKey === resoKey) {
        return key;
      }
    }
    return undefined;
  };

  const translatedMediaRecord: any = {};

  for (const key in mediaRecord) {
    // Remove 'CWA' prefix (first 3 characters) and get translation
    const translatedKey = getTranslationKey(key.substring(3));

    if (translatedKey) {
      translatedMediaRecord[translatedKey] = mediaRecord[key];
    }
  }

  return translatedMediaRecord;
};

